import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  User, Mail, Phone, MapPin, Home, Calendar, Clock, Globe, Shield,
  Activity, Timer, ClipboardList, AlertTriangle, TrendingUp, Star
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ComprehensiveUserProfile } from './types';

interface OverviewTabProps {
  profile: ComprehensiveUserProfile;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ profile }) => {
  const { user, authData, focusOlderAdult, thriveScore, circleMemberships, aggregateStats, drivingAssessments, tugTests } = profile;

  // Check if engagement data has any real values
  const hasEngagementData = false; // Currently stubbed, so always false

  return (
    <div className="space-y-6">
      {/* Top Row: Profile + Auth Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User Profile Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              Profile Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>{user?.email || 'No email'}</span>
            </div>
            {user?.phone && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-4 w-4" />
                <span>{user.phone}</span>
              </div>
            )}
            {(user?.city || user?.state || user?.zipCode) && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{[user.city, user.state, user.zipCode].filter(Boolean).join(', ')}</span>
              </div>
            )}
            {user?.homeType && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Home className="h-4 w-4" />
                <span>{user.homeType?.replace(/_/g, ' ')}{user.homeLevels ? ` (${user.homeLevels} levels)` : ''}</span>
              </div>
            )}
            {user?.preferredName && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4" />
                <span>Preferred: {user.preferredName}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Joined {user?.createdAt ? format(new Date(user.createdAt), 'MMM d, yyyy') : 'Unknown'}</span>
            </div>
            <div className="pt-2 border-t border-border flex flex-wrap gap-1">
              <Badge variant={focusOlderAdult ? 'default' : 'secondary'}>
                {focusOlderAdult ? 'Older Adult' : 'Family/Other'}
              </Badge>
              {circleMemberships?.map((m) => (
                <Badge key={m.id} variant="outline" className="text-xs">
                  {m.role?.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Auth & Session Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Authentication & Sessions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {authData ? (
              <>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>Last Sign-in: {authData.lastSignInAt 
                    ? formatDistanceToNow(new Date(authData.lastSignInAt), { addSuffix: true })
                    : 'Never'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span>Email Confirmed: {authData.emailConfirmedAt 
                    ? format(new Date(authData.emailConfirmedAt), 'MMM d, yyyy')
                    : 'Not confirmed'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Activity className="h-4 w-4" />
                  <span>Active Sessions: {authData.activeSessions}</span>
                </div>
                {authData.lastSessionInfo && (
                  <>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Globe className="h-4 w-4" />
                      <span className="truncate">IP: {authData.lastSessionInfo.ip || 'Unknown'}</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <User className="h-4 w-4 mt-0.5" />
                      <span className="text-xs line-clamp-2">{authData.lastSessionInfo.userAgent || 'Unknown device'}</span>
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">No auth data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* ThriveScore */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Timer className="h-4 w-4 text-cyan-500" />
            <span className="text-xs font-medium">ThriveScore</span>
          </div>
          {thriveScore?.current ? (
            <div>
              <p className="text-2xl font-bold">{thriveScore.current.overall}</p>
              <div className="flex gap-2 text-xs text-muted-foreground mt-1">
                <span>W: {thriveScore.current.wellness}</span>
                <span>H: {thriveScore.current.homeSafety}</span>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No score</p>
          )}
        </Card>

        {/* Rooms Scanned */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="h-4 w-4 text-amber-500" />
            <span className="text-xs font-medium">Rooms Scanned</span>
          </div>
          <p className="text-2xl font-bold">{aggregateStats?.totalRooms || 0}</p>
        </Card>

        {/* Total Issues */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span className="text-xs font-medium">Total Issues</span>
          </div>
          <p className="text-2xl font-bold">{aggregateStats?.totalIssues || 0}</p>
          <p className="text-xs text-destructive mt-1">{aggregateStats?.highImpactIssues || 0} high impact</p>
        </Card>

        {/* Driving Assessments */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-4 w-4 text-blue-500" />
            <span className="text-xs font-medium">Driving Tests</span>
          </div>
          <p className="text-2xl font-bold">{drivingAssessments?.length || 0}</p>
        </Card>

        {/* TUG Tests */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Timer className="h-4 w-4 text-green-500" />
            <span className="text-xs font-medium">TUG Tests</span>
          </div>
          <p className="text-2xl font-bold">{tugTests?.length || 0}</p>
        </Card>

        {/* Total Assessments */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-purple-500" />
            <span className="text-xs font-medium">All Assessments</span>
          </div>
          <p className="text-2xl font-bold">{aggregateStats?.totalAssessments || 0}</p>
        </Card>
      </div>

      {/* Engagement Summary - Placeholder when no data */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Star className="h-4 w-4 text-primary" />
            Engagement Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasEngagementData ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Future: Active streaks, video activity, points breakdown */}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No engagement data recorded yet</p>
              <p className="text-xs mt-1">Streaks, video activity, and points will appear here once tracked</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
